// @flow

import _ from 'lodash';

import { showSABToast } from 'src/integrations/shopify-app-bridge/toast';
import { FILTER_TREE_MESSAGE } from 'src/constants/filter-tree';
import type {
  ListFilterTreeState,
  ListFilterTreeAction
} from 'stores/flow-type/filter-tree/management.type';

import {
  LIST_FILTER_TREE_REQUEST,
  LIST_FILTER_TREE_SUCCESS,
  LIST_FILTER_TREE_FAILURE,
  SEARCH_FILTER_TREE_REQUEST,
  SEARCH_FILTER_TREE_SUCCESS,
  SEARCH_FILTER_TREE_FAILURE,
  CHANGE_FILTER_TREE_STATUS_REQUEST,
  CHANGE_FILTER_TREE_STATUS_SUCCESS,
  CHANGE_FILTER_TREE_STATUS_FAILURE,
  CLONE_FILTER_TREE_REQUEST,
  CLONE_FILTER_TREE_SUCCESS,
  CLONE_FILTER_TREE_FAILURE,
  REMOVE_FILTER_TREE_REQUEST,
  REMOVE_FILTER_TREE_SUCCESS,
  REMOVE_FILTER_TREE_FAILURE,
  LIST_FILTER_TREE_INFO_REQUEST,
  LIST_FILTER_TREE_INFO_SUCCESS,
  LIST_FILTER_TREE_INFO_FAILURE,
} from 'stores/flow-type/filter-tree/management.type';

const initialState: ListFilterTreeState = {
  loading: false,
  searching: false,
  cloning: false,
  removing: false,
  removedFilterId: '',
  updatingStatus: false,
  success: false,
  error: undefined,
  total: 0,
  customUnlockTotal: 0,
  defaultFilterTrees: [],
  searchFilterTrees: [],
  customFilterTrees: [],
  clonedFilterTrees: [],
  listFilterTreeInfo: [],
  listFilterTreeInfoLoading: false,
  listFilterTreeInfoSuccess: false,
  listFilterTreeInfoError: undefined
};

const ManagementReducers = (
  state: ListFilterTreeState = initialState,
  action: ListFilterTreeAction
) => {
  switch (action.type) {
    case LIST_FILTER_TREE_REQUEST:
      return { ...state, loading: true };
    case LIST_FILTER_TREE_SUCCESS: {
      const { data, q, skip } = action.payload;

      // Get default filter
      const defaultFilterTrees = _.filter(data.data, { isDefault: true });

      // Get search filter
      const searchFilterTrees = _.filter(data.data, { isDefaultSearch: true });

      // Get custom filter
      const customFilterTrees = _.remove(data.data, filterTree => {
        return !filterTree.isDefault && !filterTree.isDefaultSearch;
      });

      return skip === 0 && q === ''
        ? {
          ...state,
          loading: false,
          success: true,
          error: undefined,
          defaultFilterTrees,
          searchFilterTrees,
          customFilterTrees,
          total: data.total,
          customUnlockTotal: data.customUnlockTotal
        }
        : {
          ...state,
          loading: false,
          success: true,
          error: undefined,
          customFilterTrees,
          total: data.total,
          customUnlockTotal: data.customUnlockTotal
        };
    }
    case LIST_FILTER_TREE_FAILURE:
      return {
        ...state,
        loading: false,
        success: false,
        error: action.payload
      };
    // Search filter tree
    case SEARCH_FILTER_TREE_REQUEST:
      return { ...state, searching: true };
    case SEARCH_FILTER_TREE_SUCCESS: {
      const { data } = action.payload;

      return {
        ...state,
        searching: false,
        success: true,
        error: undefined,
        customFilterTrees: data.data,
        total: data.total
      };
    }
    case SEARCH_FILTER_TREE_FAILURE:
      return {
        ...state,
        searching: false,
        success: false,
        error: action.payload
      };
    // Update status
    case CHANGE_FILTER_TREE_STATUS_REQUEST:
      return { ...state, updatingStatus: true };
    case CHANGE_FILTER_TREE_STATUS_SUCCESS: {
      const { status, i18n } = action.payload;

      if (status === 'active') {
        showSABToast(i18n.translate(FILTER_TREE_MESSAGE.ACTIVE_FILTER_SUCCESS));
      } else {
        showSABToast(i18n.translate(FILTER_TREE_MESSAGE.DISABLE_FILTER_SUCCESS));
      }

      return {
        ...state,
        updatingStatus: false,
        success: true,
        error: undefined
      };
    }
    case CHANGE_FILTER_TREE_STATUS_FAILURE: {
      const { status, i18n } = action.payload;

      if (status === 'active') {
        showSABToast(i18n.translate(FILTER_TREE_MESSAGE.ACTIVE_FILTER_FAILED));
      } else {
        showSABToast(i18n.translate(FILTER_TREE_MESSAGE.DISABLE_FILTER_FAILED));
      }

      return {
        ...state,
        updatingStatus: false,
        success: false,
        error: action.payload
      };
    }
    // Clone filter tree
    case CLONE_FILTER_TREE_REQUEST:
      return { ...state, cloning: true, success: false };
    case CLONE_FILTER_TREE_SUCCESS: {
      const { data } = action.payload;

      const clonedFilterTrees = data || [];
      const count = clonedFilterTrees.length || 0;
      const customUnlockTotal = state.customUnlockTotal + count;

      return {
        ...state,
        customUnlockTotal,
        cloning: false,
        success: true,
        error: undefined,
        clonedFilterTrees
      };
    }
    case CLONE_FILTER_TREE_FAILURE:
      return {
        ...state,
        cloning: false,
        success: false,
        error: action.payload
      };
    // Remove filter tree
    case REMOVE_FILTER_TREE_REQUEST:
      return { ...state, removing: true, success: false };
    case REMOVE_FILTER_TREE_SUCCESS: {
      const { filterId, i18n } = action.payload;

      showSABToast(i18n.translate(FILTER_TREE_MESSAGE.DELETE_FILTER_SUCCESS));
      const customUnlockTotal = state.customUnlockTotal - 1;

      return {
        ...state,
        customUnlockTotal,
        removing: false,
        removedFilterId: filterId,
        success: true,
        error: undefined
      };
    }
    case REMOVE_FILTER_TREE_FAILURE: {
      const { i18n } = action.payload;
      showSABToast(i18n.translate(FILTER_TREE_MESSAGE.DELETE_FILTER_FAILED));

      return {
        ...state,
        removing: false,
        success: false,
        error: action.payload
      };
    }

    case LIST_FILTER_TREE_INFO_REQUEST:
      return { ...state, listFilterTreeInfoLoading: true };
    case LIST_FILTER_TREE_INFO_SUCCESS: {
      return {
        ...state,
        listFilterTreeInfoLoading: false,
        listFilterTreeInfoSuccess: true,
        listFilterTreeInfo: action.payload
      };
    }
    case LIST_FILTER_TREE_INFO_FAILURE:
      return {
        ...state,
        listFilterTreeInfoLoading: false,
        listFilterTreeInfoSuccess: false,
        listFilterTreeInfoError: action.payload
      };

    default:
      return state;
  }
};

export default ManagementReducers;
